import { Card, CardContent } from "@mui/material";
import { useAuth } from "@ssf/auth-components";
import {
  ContentContainer,
  Loading,
  markdownToHtmlSync,
  useTrackPageChange,
} from "@ssf/components";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router";

import { generatePath, routes, useY2cTournaments } from "../lib/index.js";
import { Announcement } from "./index.js";

const HomePage = lazy(() => import("../pages/HomePage.jsx"));
const TournamentPage = lazy(() => import("../pages/TournamentPage.jsx"));
const MatchesPage = lazy(() => import("../pages/MatchesPage.jsx"));
const TeamPage = lazy(() => import("../pages/TeamPage.jsx"));
const GamePage = lazy(() => import("../pages/GamePage.jsx"));
const MatchPage = lazy(() => import("../pages/MatchPage.jsx"));
const RegPage = lazy(() => import("../pages/RegPage.jsx"));
const RegTeamPage = lazy(() => import("../pages/RegTeamPage.jsx"));
const CreateClubPage = lazy(() => import("../pages/CreateClubPage.jsx"));
const MyPage = lazy(() => import("../pages/MyPage.jsx"));
const MembersWithoutTeams = lazy(
  () => import("../pages/MembersWithoutTeams.jsx"),
);
const SchoolStatsPage = lazy(() => import("../pages/SchoolStatsPage.jsx"));
const SchoolDetailPage = lazy(() => import("../pages/SchoolDetailPage.jsx"));

export const Router = () => {
  const { i18n } = useTranslation("pages");
  const { user, isInitiated } = useAuth();
  const { isRegOpen, isLoading: isRegLoading } = useY2cTournaments();

  useTrackPageChange();

  const pages = (i18n.store.data[i18n.language].pages || []) as {
    path: string;
    content: string;
  }[];

  return (
    <Routes>
      <Route
        path={routes.home}
        element={
          !isInitiated || isRegLoading ? (
            <Loading />
          ) : user && isRegOpen ? (
            <Navigate
              to={generatePath("myPage", { memberId: user.profile.sub })}
              replace
            />
          ) : (
            <Suspense fallback={<Loading />}>
              <HomePage />
            </Suspense>
          )
        }
      />
      <Route
        path={routes.information}
        element={
          <Suspense fallback={<Loading />}>
            <HomePage />
          </Suspense>
        }
      />
      {pages.map(({ path, content }) => (
        <Route
          key={path}
          path={path}
          element={
            <ContentContainer sx={{ my: 8 }}>
              <Announcement sx={{ mb: 4 }} />
              <Card>
                <CardContent
                  sx={{ py: 0 }}
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtmlSync(content).value.toString(),
                  }}
                />
              </Card>
            </ContentContainer>
          }
        />
      ))}
      {[
        routes.tournament,
        routes.tournamentGroup,
        routes.tournamentGroupRound,
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <Suspense fallback={<Loading />}>
              <TournamentPage />
            </Suspense>
          }
        />
      ))}
      <Route
        path={routes.matches}
        element={
          <Suspense fallback={<Loading />}>
            <MatchesPage />
          </Suspense>
        }
      />
      <Route
        path={routes.match}
        element={
          <Suspense fallback={<Loading />}>
            <MatchPage />
          </Suspense>
        }
      />
      <Route
        path={routes.game}
        element={
          <Suspense fallback={<Loading />}>
            <GamePage />
          </Suspense>
        }
      />
      <Route
        path={routes.team}
        element={
          <Suspense fallback={<Loading />}>
            <TeamPage />
          </Suspense>
        }
      />
      <Route
        path={routes.reg}
        element={
          <Suspense fallback={<Loading />}>
            <RegPage />
          </Suspense>
        }
      />

      <Route
        path={routes.regTeam}
        element={
          <Suspense fallback={<Loading />}>
            <RegTeamPage />
          </Suspense>
        }
      />

      <Route
        path={routes.createClub}
        element={
          <Suspense fallback={<Loading />}>
            <CreateClubPage />
          </Suspense>
        }
      />

      <Route
        path={routes.myPage}
        element={
          <Suspense fallback={<Loading />}>
            <MyPage />
          </Suspense>
        }
      />

      <Route
        path={routes.membersWithoutTeamsPage}
        element={
          <Suspense fallback={<Loading />}>
            <MembersWithoutTeams />
          </Suspense>
        }
      />

      <Route
        path={routes.schoolStats}
        element={
          !isInitiated ? (
            <Loading />
          ) : (
            <Suspense fallback={<Loading />}>
              <SchoolStatsPage />
            </Suspense>
          )
        }
      />

      <Route
        path={routes.schoolDetail}
        element={
          <Suspense fallback={<Loading />}>
            <SchoolDetailPage />
          </Suspense>
        }
      />
    </Routes>
  );
};
